import { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  useTheme,
  TableRow,
  TableCell,
  Avatar,
  Tooltip,
  IconButton,
  Dialog,
  DialogContent
} from "@mui/material";

import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShirt,
  faCircleCheck,
  faClockRotateLeft,
  faTriangleExclamation,
  faQuestion,
  faBoxOpen,
  faTag,
  faBuilding
} from "@fortawesome/free-solid-svg-icons";

import { hexToRgba } from "@aclymatepackages/converters";
import { formatDecimal, ucFirstLetters } from "@aclymatepackages/formatters";
import { productCategories } from "@aclymatepackages/lists";
import { calculateMergedProductFootprint } from "@aclymatepackages/products";
import { displayProductFootprintCategories } from "@aclymatepackages/add-ons";
import {
  Autocomplete,
  ProductFootprintCategoryAvatar,
  Select
} from "@aclymatepackages/atoms";

import ProductReport from "./ProductReport";
import PlatformProductDetailsSlider from "./PlatformProductDetailsSlider";
import BomBulkUploadFlow from "./bom-bulk-upload/BomBulkUploadFlow";
import { exportProductsGapWorkbook } from "./bom-bulk-upload/bomGapExport";
import VendorDetailsSlider from "../dashboard/views/vendors/VendorDetailsSlider";
import Link from "../../atoms/mui/Link";

import LoadingCardLayoutBox from "../../layouts/LoadingCardLayoutBox";
import NoDataLayout from "../../layouts/NoDataLayout";
import DashboardViewLayout from "../../layouts/DashboardViewLayout";
import ViewGraphCard from "../../layouts/ViewGraphCard";

import SortableTable, {
  makeColumnObj
} from "../../modules/tables/SortableTable";
import ViewBarChart from "../../modules/charts/ViewBarChart";
import EmissionsCustomTooltip from "../../modules/charts/EmissionsCustomTooltip";
import NewProductForm from "../../inputs/products/NewProductForm";
import useStatusFilterChips from "../../hooks/statusFilterChips";
import NullableInput from "../../atoms/inputs/NullableInput";
import MultiPartForm from "../../inputs/flows/MultipartForm";
import useBomProductsCsvUploader from "../../hooks/bomProductsCsvUploader";
import ButtonMenu from "../../atoms/buttons/ButtonMenu";

import {
  useCachedDisplayData,
  useAccountData,
  useMutateCachedCollection
} from "../../../helpers/firebase";
import useProductsEmissionsFactors, {
  ProductsEmissionsFactorsContextProvider
} from "../../../helpers/contexts/ProductsEmissionsFactors";
import useEmissionsContext from "../../../helpers/contexts/emissions";
import usePlatformLayoutContext from "../../../helpers/contexts/platformLayout";
import {
  getProductStatus,
  restoreOriginalWeights
} from "../../../helpers/components/products";
import usePermissions from "../../../helpers/hooks/usePermissions";

const mergeById = (...lists) => {
  const seen = new Set();
  return lists.flat().filter(({ id }) => {
    if (seen.has(id)) {
      return false;
    }
    seen.add(id);
    return true;
  });
};

const useDisplayProducts = () => {
  const [{ emissionsFactors }, emissionsFactorsLoading] =
    useProductsEmissionsFactors();
  const [{ id: accountId }] = useAccountData();

  const [ownedProducts, ownedProductsLoading] =
    useCachedDisplayData("product-components");
  const [vendorAssignedProducts, vendorAssignedProductsLoading] =
    useCachedDisplayData("vendor-assigned-products");
  const [ownedLifecycleEvents, ownedLifecycleEventsLoading] =
    useCachedDisplayData("product-lifecycle-events");
  const [vendorAssignedLifecycleEvents, vendorAssignedLifecycleEventsLoading] =
    useCachedDisplayData("vendor-assigned-product-lifecycle-events");
  const [customers, customersLoading] = useCachedDisplayData("customers");

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);

  useEffect(() => {
    const buildProductsArray = () => {
      const dbProducts = mergeById(ownedProducts, vendorAssignedProducts);
      const productLifecycleEvents = mergeById(
        ownedLifecycleEvents,
        vendorAssignedLifecycleEvents
      );

      const topLevelProducts = dbProducts.filter((p) => !p.parentProductId);
      const componentDocs = dbProducts.filter((p) => !!p.parentProductId);

      const customerNameByBuyerId = Object.fromEntries(
        (customers || []).map(({ buyerId, buyerName }) => [buyerId, buyerName])
      );

      const mergedDbProductsWithFootprint = topLevelProducts.map((product) => {
        const { id, buyerIds = [] } = product;
        const lifecycleEvent =
          productLifecycleEvents.find(({ productId }) => productId === id) ||
          {};

        const resolvedComponents = (product.componentRefs || [])
          .map(({ id: componentId }) =>
            componentDocs.find((c) => c.id === componentId)
          )
          .filter(Boolean);

        const productWithComponents = resolvedComponents.length
          ? { ...product, components: resolvedComponents }
          : product;

        const calculatedProduct = calculateMergedProductFootprint(
          productWithComponents,
          lifecycleEvent,
          emissionsFactors
        );

        const isOwnedAsBuyer = buyerIds.includes(accountId);
        const ownerKind = isOwnedAsBuyer ? "vendor" : "customer";
        const ownerName = isOwnedAsBuyer
          ? lifecycleEvent.vendor?.name || ""
          : customerNameByBuyerId[lifecycleEvent.ownerId] || "";

        return {
          ...calculatedProduct,
          resolvedComponents,
          packaging: restoreOriginalWeights(
            calculatedProduct.packaging,
            lifecycleEvent.packaging
          ),
          ownerKind,
          ownerName
        };
      });

      setProductsLoading(false);
      return setProducts(mergedDbProductsWithFootprint);
    };

    if (
      !ownedProductsLoading &&
      !vendorAssignedProductsLoading &&
      !ownedLifecycleEventsLoading &&
      !vendorAssignedLifecycleEventsLoading &&
      !customersLoading &&
      !emissionsFactorsLoading
    ) {
      buildProductsArray();
    }
  }, [
    accountId,
    ownedProducts,
    ownedProductsLoading,
    vendorAssignedProducts,
    vendorAssignedProductsLoading,
    ownedLifecycleEvents,
    ownedLifecycleEventsLoading,
    vendorAssignedLifecycleEvents,
    vendorAssignedLifecycleEventsLoading,
    customers,
    customersLoading,
    emissionsFactors,
    emissionsFactorsLoading
  ]);

  return [products, productsLoading];
};

const NoProductsLayout = ({
  setShowNewProductForm,
  setShowBomUploader,
  setShowBomBulkUploader
}) => {
  const { canEdit } = usePermissions();

  return (
    <NoDataLayout
      title="Build trust with transparent product carbon footprints."
      description="Track and communicate the carbon footprint of your products to build customer trust and drive sustainable purchasing decisions."
      imagePath="/images/tags-onboarding-images/products.svg"
      imageAlt="Product Carbon Footprint Illustration"
      action={
        canEdit && (
          <ButtonMenu
            color="secondary"
            menuOptions={[
              {
                label: "Create New Product",
                onClick: () => setShowNewProductForm(true)
              },
              {
                label: "Upload Products CSV",
                onClick: () => setShowBomUploader(true)
              },
              {
                label: "Bulk Upload BOMs",
                onClick: () => setShowBomBulkUploader(true)
              }
            ]}
          />
        )
      }
    />
  );
};

export const ProductsTableRow = ({ product, onClick, setReportProduct }) => {
  const { palette } = useTheme();
  const [{ isSubscribed }] = useProductsEmissionsFactors();

  const {
    name,
    sku,
    category,
    subcategory,
    materialsWeightKgs,
    packagingWeightKgs,
    totalProductWeightKgs,
    kgsCo2e,
    vendor,
    bomStatus,
    ownerKind,
    ownerName
  } = product;

  const vendorConfirmed =
    !bomStatus || bomStatus === "confirmed" || !vendor?.id;

  const ownerCell = ownerName ? (
    <Tooltip title={ownerKind === "customer" ? "Customer" : "Vendor"}>
      <Chip
        label={ownerName}
        color={ownerKind === "customer" ? "secondary" : "default"}
        size="small"
        icon={
          <FontAwesomeIcon
            icon={ownerKind === "customer" ? faBuilding : faTag}
            style={{ marginLeft: 8 }}
          />
        }
      />
    </Tooltip>
  ) : (
    ""
  );

  const { label: productCategoryLabel, subcategories } = productCategories.find(
    ({ id }) => id === category
  ) || { label: category || "Uncategorized", subcategories: [] };
  const { label: productSubcategoryLabel } = subcategories.find(
    ({ id }) => id === subcategory
  ) || { label: subcategory };

  const { tooltip, icon, colorKey } = getProductStatus(product);

  const cells = [
    `${name} (${sku})`,
    <Tooltip key="product-status-tooltip" title={tooltip}>
      <span>
        <FontAwesomeIcon
          icon={icon}
          style={{ color: palette[colorKey].main }}
          size="2x"
        />
      </span>
    </Tooltip>,
    ownerCell,
    `${productCategoryLabel}${
      productSubcategoryLabel
        ? ` > ${ucFirstLetters(productSubcategoryLabel)}`
        : ""
    }`,
    vendorConfirmed ? formatDecimal(materialsWeightKgs) : "",
    vendorConfirmed ? formatDecimal(packagingWeightKgs) : "",
    vendorConfirmed ? formatDecimal(totalProductWeightKgs) : "",
    vendorConfirmed &&
      (!isSubscribed ? (
        <Tooltip title="Subscribe to view carbon footprint data">
          <span style={{ filter: "blur(4px)" }}>####</span>
        </Tooltip>
      ) : (
        formatDecimal(kgsCo2e)
      )),
    vendorConfirmed && !!kgsCo2e ? (
      <Button
        color="secondary"
        variant="contained"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          return setReportProduct(product);
        }}
      >
        Show Footprint Report
      </Button>
    ) : (
      ""
    )
  ];

  return (
    <TableRow onClick={onClick} sx={{ cursor: "pointer" }}>
      {cells.map((cell, idx) => (
        <TableCell key={`table-cell-${idx}`}>{cell}</TableCell>
      ))}
    </TableRow>
  );
};

const ProductsChart = ({ dataArray, setSelectedObject }) => {
  const [{ isSubscribed }] = useProductsEmissionsFactors();

  return (
    <ViewBarChart
      unit="kgs"
      dataType="products"
      setSelectedObject={setSelectedObject}
      data={dataArray}
      barAreasArray={displayProductFootprintCategories}
      tooltipComponent={
        <EmissionsCustomTooltip
          blurValues={!isSubscribed}
          categoriesArray={displayProductFootprintCategories.map(
            (category) => ({
              ...category,
              customAvatar: (
                <ProductFootprintCategoryAvatar {...category} size="small" />
              )
            })
          )}
          dataArray={dataArray}
          unit="kgs"
        />
      }
      blurValues={!isSubscribed}
    />
  );
};

const ProductsBarGraph = ({ products, setSelectedProduct }) => {
  const completedProducts = products.filter(
    ({ kgsCo2e, bomStatus, vendor }) =>
      kgsCo2e && (!bomStatus || bomStatus === "confirmed" || !vendor?.id)
  );

  const productsWithLabels = completedProducts.map(
    ({ name, footprintBreakdownKgs, id }) => ({
      name,
      label: name,
      ...footprintBreakdownKgs,
      id
    })
  );

  const handleSelectedProductFromBar = (productId) => {
    const selectedProduct = products.find(({ id }) => id === productId);
    return setSelectedProduct(selectedProduct);
  };

  const graphs = [
    {
      title: "Product Carbon Footprint Breakdown",
      type: "bar",
      Graph: ProductsChart,
      graphProps: {
        setSelectedObject: handleSelectedProductFromBar
      },
      sortable: true
    }
  ];

  const hasExcludedProducts = completedProducts.length < products.length;

  return (
    <ViewGraphCard
      graphs={graphs}
      dataArray={productsWithLabels}
      subtitle={
        hasExcludedProducts
          ? "NOTE: Only products with a completed carbon footprint are shown in the chart."
          : undefined
      }
    />
  );
};

const BomProductsUploader = ({ open, setOpen }) => {
  const steps = useBomProductsCsvUploader({ open, setOpen });

  return (
    <MultiPartForm
      type="product"
      forms={steps}
      onClose={() => setOpen(false)}
      submitLoadingText="Please wait while we upload your products CSV..."
    />
  );
};

const getProductStatusKey = ({ kgsCo2e, bomStatus, ownerKind }) => {
  if (bomStatus === "confirmed" || bomStatus === "requested") {
    return bomStatus;
  }
  if (bomStatus === "pending" && ownerKind === "customer") {
    return "pending";
  }
  if (!kgsCo2e) {
    return "incomplete";
  }
  return "unconfirmed";
};

const DisplayProducts = ({
  products,
  openNewProductForm,
  setShowBomUploader,
  setShowBomBulkUploader
}) => {
  const { palette } = useTheme();
  const { canEdit } = usePermissions();

  const [{ isSubscribed }] = useProductsEmissionsFactors();
  const [{ name: companyName }] = useAccountData();

  const history = useHistory();
  const location = useLocation();
  const selectedProductId = new URLSearchParams(location.search).get("id");
  const selectedProduct =
    products.find((p) => p.id === selectedProductId) ?? null;
  const setSelectedProduct = (product) =>
    product
      ? history.replace({ search: `?id=${product.id}` })
      : history.replace({ search: "" });

  const { vendors } = useEmissionsContext();
  const { activateSnackbar } = usePlatformLayoutContext();

  const [reportProduct, setReportProduct] = useState(null);
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const selectedVendorData =
    vendors.find((v) => v.id === selectedVendorId) ?? null;
  const selectedVendorLinkedProducts = selectedVendorData
    ? products.filter((p) => p.vendor?.id === selectedVendorData.vendorId)
    : [];
  const [searchString, setSearchString] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterSubcategory, setFilterSubcategory] = useState("");
  const [filterOwner, setFilterOwner] = useState(null);

  const productStatusesMap = {
    confirmed: {
      id: "confirmed",
      icon: faCircleCheck,
      name: "BOM Confirmed",
      color: palette.secondary.main
    },
    requested: {
      id: "requested",
      icon: faClockRotateLeft,
      name: "BOM Requested",
      color: palette.warning.main
    },
    pending: {
      id: "pending",
      icon: faClockRotateLeft,
      name: "BOM Pending",
      color: palette.warning.main
    },
    incomplete: {
      id: "incomplete",
      icon: faTriangleExclamation,
      name: "Incomplete",
      color: palette.error.main
    },
    unconfirmed: {
      id: "unconfirmed",
      icon: faQuestion,
      name: "Unconfirmed",
      color: palette.warning.dark
    }
  };

  const productsWithStatus = products.map((product) => ({
    ...product,
    status: getProductStatusKey(product)
  }));

  const { filterChips, filterByChips } = useStatusFilterChips(
    productsWithStatus,
    productStatusesMap,
    "Products"
  );

  const categoryOptions = [
    ...new Set(products.map(({ category }) => category).filter(Boolean))
  ].map((catId) => {
    const cat = productCategories.find(({ id }) => id === catId);
    return { label: cat?.label || catId, value: catId };
  });

  const selectedCategory = productCategories.find(
    ({ id }) => id === filterCategory
  );

  const subcategoryOptions = (selectedCategory?.subcategories || []).map(
    ({ id, label }) => ({ label, value: id })
  );

  const ownerOptions = [
    ...new Map(
      products
        .filter(({ ownerName }) => !!ownerName)
        .map(({ ownerName, ownerKind }) => [
          `${ownerKind}:${ownerName}`,
          { id: `${ownerKind}:${ownerName}`, name: ownerName, ownerKind }
        ])
    ).values()
  ].sort((a, b) => a.name.localeCompare(b.name));

  const buildFilterRows = () => [
    {
      icon: faBoxOpen,
      title: "Category",
      filterComponent: (
        <Select
          value={filterCategory}
          editValue={(val) => {
            setFilterCategory(val);
            setFilterSubcategory("");
          }}
          options={categoryOptions}
          size="small"
          style={{ minWidth: "200px" }}
          label="Select a Category"
        />
      )
    },
    {
      icon: faTag,
      title: "Subcategory",
      filterComponent: (
        <Select
          value={filterSubcategory}
          editValue={setFilterSubcategory}
          options={subcategoryOptions}
          size="small"
          style={{ minWidth: "200px" }}
          label="Select a Subcategory"
        />
      )
    },
    {
      icon: faBuilding,
      title: "Owner",
      filterComponent: (
        <Autocomplete
          value={filterOwner}
          setValue={setFilterOwner}
          availableOptions={ownerOptions}
          size="small"
          style={{ minWidth: "200px" }}
          label="Select an Owner"
        />
      )
    }
  ];

  const filteredProducts = productsWithStatus
    .filter(filterByChips)
    .filter(({ name, sku, ownerName }) => {
      if (!searchString) {
        return true;
      }
      const lower = searchString.toLowerCase();
      return (
        name?.toLowerCase().includes(lower) ||
        sku?.toLowerCase().includes(lower) ||
        ownerName?.toLowerCase().includes(lower)
      );
    })
    .filter(({ category }) => !filterCategory || category === filterCategory)
    .filter(
      ({ subcategory }) =>
        !filterSubcategory || subcategory === filterSubcategory
    )
    .filter(
      ({ ownerName, ownerKind }) =>
        !filterOwner ||
        (ownerName === filterOwner.name && ownerKind === filterOwner.ownerKind)
    );

  const isFiltered = !!filterCategory || !!filterSubcategory || !!filterOwner;

  const productsTableColumns = [
    makeColumnObj("PRODUCT NAME (SKU)", "name", true),
    makeColumnObj("STATUS", "status", true),
    makeColumnObj("OWNER", "ownerName", true),
    makeColumnObj("CATEGORY > SUBCATEGORY", "category", true),
    makeColumnObj("PRODUCT WEIGHT (kgs)", "materialsWeightKgs", true),
    makeColumnObj("PACKAGING WEIGHT (kgs)", "packagingWeightKgs", true),
    makeColumnObj("TOTAL WEIGHT (kgs)", "totalProductWeightKgs", true),
    makeColumnObj("CARBON FOOTPRINT (kgs)", "kgsCo2e", true),
    makeColumnObj("ACTIONS")
  ];

  return (
    <>
      {reportProduct && (
        <ProductReport
          setShowReport={setReportProduct}
          companyName={companyName}
          product={reportProduct}
          isFinalized={
            !reportProduct.vendor?.id || reportProduct.bomStatus === "confirmed"
          }
        />
      )}
      {selectedProduct && (
        <PlatformProductDetailsSlider
          setReportProduct={setReportProduct}
          selectedProduct={selectedProduct}
          setSelectedProduct={setSelectedProduct}
          otherProducts={products.filter(({ id }) => id !== selectedProduct.id)}
          setSelectedVendor={(vendor) =>
            setSelectedVendorId(vendor?.id ?? null)
          }
        />
      )}
      {selectedVendorData && (
        <VendorDetailsSlider
          selectedVendor={selectedVendorData}
          setSelectedVendor={() => setSelectedVendorId(null)}
          linkedProducts={selectedVendorLinkedProducts}
        />
      )}
      <Box
        sx={{
          height: "100%",
          width: "100%"
        }}
      >
        <DashboardViewLayout
          type="products"
          title="Products"
          avatar={
            <Avatar style={{ backgroundColor: palette.secondary.main }}>
              <FontAwesomeIcon icon={faShirt} />
            </Avatar>
          }
          alerts={
            !isSubscribed && [
              {
                severity: "warning",
                title: "You are not subscribed to product carbon footprinting",
                subtitle:
                  "You can add as many products as you like, but you'll need a subscription to view their carbon footprint",
                action: (
                  <Link href="https://calendly.com/tyler-aclymate/demo-website">
                    <Button color="warning">Schedule a Demo</Button>
                  </Link>
                )
              }
            ]
          }
          chips={filterChips}
          isFiltered={isFiltered}
          filterPopperProps={{
            filterRows: buildFilterRows(),
            resetFilters: () => {
              setFilterCategory("");
              setFilterSubcategory("");
              setFilterOwner(null);
            }
          }}
          premiumActions={[
            <NullableInput
              key="search-products"
              value={searchString}
              setValue={setSearchString}
              label="Search products"
            />,
            <Tooltip
              key="download-data-gaps"
              title="Download the filtered list with each product's missing data and assumptions"
            >
              <span>
                <IconButton
                  disabled={!filteredProducts.length}
                  onClick={() =>
                    exportProductsGapWorkbook(filteredProducts, {
                      fileName: `${companyName || "products"}-data-gaps.xlsx`
                    }).catch((err) => {
                      console.error("Product gap export failed:", err);
                      activateSnackbar({
                        message:
                          "Couldn't generate the data-gap workbook. Please try again or contact support.",
                        severity: "error"
                      });
                    })
                  }
                >
                  <FileDownloadIcon />
                </IconButton>
              </span>
            </Tooltip>
          ]}
          primaryAction={
            canEdit && (
              <ButtonMenu
                color="secondary"
                menuOptions={[
                  {
                    label: "Create New Product",
                    onClick: openNewProductForm
                  },
                  {
                    label: "Upload Products CSV",
                    onClick: () => setShowBomUploader(true)
                  },
                  {
                    label: "Bulk Upload BOMs",
                    onClick: () => setShowBomBulkUploader(true)
                  }
                ]}
              />
            )
          }
          table={
            <SortableTable
              rows={filteredProducts}
              rowComponent={(product) => (
                <ProductsTableRow
                  key={`product-row-${product.id}`}
                  product={product}
                  onClick={() => setSelectedProduct(product)}
                  setReportProduct={setReportProduct}
                />
              )}
              columns={productsTableColumns}
            />
          }
          graph={
            products.some(
              ({ kgsCo2e, bomStatus, vendor }) =>
                kgsCo2e &&
                (!bomStatus || bomStatus === "confirmed" || !vendor?.id)
            ) ? (
              <ProductsBarGraph
                products={filteredProducts}
                setSelectedProduct={setSelectedProduct}
              />
            ) : null
          }
        />
      </Box>
    </>
  );
};

const ProductsBlock = () => {
  const { palette } = useTheme();
  const mutateCachedCollection = useMutateCachedCollection();

  const [products, productsLoading] = useDisplayProducts();
  const [showNewProductForm, setShowNewProductForm] = useState(false);
  const [showBomUploader, setShowBomUploader] = useState(false);
  const [showBomBulkUploader, setShowBomBulkUploader] = useState(false);

  const closeBulkUploader = () => {
    setShowBomBulkUploader(false);
    mutateCachedCollection("product-components");
    mutateCachedCollection("product-lifecycle-events");
  };

  const productDisplay =
    products.length > 0 ? (
      <DisplayProducts
        products={products}
        openNewProductForm={() => setShowNewProductForm(true)}
        setShowBomUploader={setShowBomUploader}
        setShowBomBulkUploader={setShowBomBulkUploader}
      />
    ) : (
      <NoProductsLayout
        setShowNewProductForm={setShowNewProductForm}
        setShowBomUploader={setShowBomUploader}
        setShowBomBulkUploader={setShowBomBulkUploader}
      />
    );

  return (
    <>
      {showNewProductForm && (
        <NewProductForm
          setShowForm={setShowNewProductForm}
          existingProducts={products}
        />
      )}
      {showBomUploader && (
        <BomProductsUploader
          open={showBomUploader}
          setOpen={setShowBomUploader}
        />
      )}
      <Dialog
        open={showBomBulkUploader}
        onClose={closeBulkUploader}
        fullWidth
        maxWidth="lg"
      >
        <DialogContent>
          <BomBulkUploadFlow onClose={closeBulkUploader} />
        </DialogContent>
      </Dialog>
      <LoadingCardLayoutBox
        style={{
          backgroundColor: hexToRgba(palette.primary.main, 0.1),
          display: "flex",
          alignItems: "center",
          minHeight: "100%"
        }}
        isLoading={productsLoading}
      >
        {productDisplay}
      </LoadingCardLayoutBox>
    </>
  );
};

const Products = () => {
  return (
    <ProductsEmissionsFactorsContextProvider>
      <ProductsBlock />
    </ProductsEmissionsFactorsContextProvider>
  );
};

export default Products;
